interface WatermarkOptions {
  text: string | string[]
  font?: string
  color?: string
  rotate?: number
  opacity?: number
  gapX?: number
  gapY?: number
  scale?: number
  zIndex?: number
}

class Watermark {
  private el: HTMLDivElement | null = null
  private observer: MutationObserver | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private options: WatermarkOptions

  constructor(options: WatermarkOptions) {
    this.options = {
      font: '14px Arial',
      color: 'rgb(128,128,128)',
      rotate: -45,
      opacity: 0.05,
      gapX: 200,
      gapY: 150,
      scale: 1,
      zIndex: 9999,
      ...options
    }
  }

  private parseFont(font: string) {
    const normalized = String(font || '').trim()
    const match = normalized.match(/(\d+(?:\.\d+)?)px/i)
    if (!match || typeof match.index !== 'number') {
      return { size: 14, prefix: '', suffix: normalized || 'Arial' }
    }

    const size = Number(match[1])
    const prefix = normalized.slice(0, match.index).trim()
    const suffix = normalized.slice(match.index + match[0].length).trim()

    return {
      size: Number.isFinite(size) ? size : 14,
      prefix,
      suffix: suffix || 'Arial'
    }
  }

  private measureMaxLineWidth(lines: string[], font: string) {
    const measureCanvas = document.createElement('canvas')
    const measureCtx = measureCanvas.getContext('2d')
    if (!measureCtx) return 0
    measureCtx.font = font
    let maxTextWidth = 0
    for (const line of lines) {
      maxTextWidth = Math.max(maxTextWidth, measureCtx.measureText(line).width)
    }
    return maxTextWidth
  }

  private normalizeTextLines(text: string | string[]) {
    if (Array.isArray(text)) return text.filter(Boolean)
    const raw = String(text || '').trim()
    if (!raw) return ''
    return raw
  }

  private wrapTextToLines(text: string, font: string, maxWidth: number) {
    const raw = String(text || '').trim()
    if (!raw) return []
    const safeMaxWidth = Math.max(1, Number(maxWidth || 1))

    const measureCanvas = document.createElement('canvas')
    const measureCtx = measureCanvas.getContext('2d')
    if (!measureCtx) return [raw]
    measureCtx.font = font

    const chars = Array.from(raw)
    const lines: string[] = []
    let current = ''

    for (const ch of chars) {
      if (!current && ch.trim() === '') {
        continue
      }
      const candidate = current + ch
      if (!current || measureCtx.measureText(candidate).width <= safeMaxWidth) {
        current = candidate
      } else {
        lines.push(current.trimEnd())
        current = ch.trim() === '' ? '' : ch
      }
    }

    if (current) lines.push(current.trimEnd())
    return lines.filter(Boolean)
  }

  /** 创建 canvas 水印 */
  private createWatermarkBase64() {
    const { text, font, color, rotate, opacity, gapX, gapY, scale } = this.options
    const resolvedScale = Math.max(0.1, Number(scale ?? 1))

    const resolvedFont = font || '10px Arial'
    const resolvedGapX = Math.max(1, Number(gapX || 320))
    const resolvedGapY = Math.max(1, Number(gapY || 220))
    const resolvedRotate = Number(rotate || -30)
    const normalizedText = this.normalizeTextLines(text)

    const paddingX = 16 * resolvedScale
    const paddingY = 14 * resolvedScale
    const tileW = resolvedGapX * resolvedScale
    const tileH = resolvedGapY * resolvedScale
    const textMaxWidth = Math.max(1, tileW - paddingX * 2)

    const { prefix, suffix, size } = this.parseFont(resolvedFont)
    const minFontSize = 5
    let effectiveSize = Math.max(minFontSize, Math.floor(size || 14))
    const rad = (Math.PI / 180) * resolvedRotate
    const cos = Math.abs(Math.cos(rad))
    const sin = Math.abs(Math.sin(rad))

    // 根据屏幕宽度动态调整最小字体和起始字体
    const isMobile = window.innerWidth < 768
    const adjustedMinFontSize = isMobile ? 4 : 6
    const adjustedStartFontSize = isMobile ? Math.min(6, size || 6) : Math.floor(size || 14)
    effectiveSize = Math.max(adjustedMinFontSize, adjustedStartFontSize)

    let resolvedLines: string[] = []
    let resolvedFontSize = effectiveSize * resolvedScale

    while (effectiveSize > adjustedMinFontSize) {
      const candidateFontSize = effectiveSize * resolvedScale
      const candidateFont = `${prefix ? `${prefix} ` : ''}${candidateFontSize}px ${suffix}`
      const candidateLines = Array.isArray(normalizedText)
        ? normalizedText
        : this.wrapTextToLines(normalizedText, candidateFont, textMaxWidth)

      const maxTextWidth = this.measureMaxLineWidth(candidateLines, candidateFont)
      const lineHeight = Math.ceil(candidateFontSize * 1.3)
      const totalTextHeight = lineHeight * Math.max(1, candidateLines.length)
      const baseW = Math.ceil(maxTextWidth + paddingX * 2)
      const baseH = Math.ceil(totalTextHeight + paddingY * 2)
      const bboxW = Math.ceil(baseW * cos + baseH * sin)
      const bboxH = Math.ceil(baseW * sin + baseH * cos)

      if (bboxW <= tileW && bboxH <= tileH) {
        resolvedLines = candidateLines
        resolvedFontSize = candidateFontSize
        break
      }
      effectiveSize -= 0.5
    }

    if (!resolvedLines.length) {
      resolvedFontSize = effectiveSize * resolvedScale
    }

    const effectiveFont = `${prefix ? `${prefix} ` : ''}${resolvedFontSize}px ${suffix}`
    const lineHeight = Math.ceil(resolvedFontSize * 1.3)

    if (!resolvedLines.length) {
      resolvedLines = Array.isArray(normalizedText)
        ? normalizedText
        : this.wrapTextToLines(normalizedText, effectiveFont, textMaxWidth)
    }

    const canvas = document.createElement('canvas')
    const ratio = window.devicePixelRatio || 1

    canvas.width = tileW * ratio
    canvas.height = tileH * ratio

    const ctx = canvas.getContext('2d')!
    ctx.scale(ratio, ratio)
    ctx.globalAlpha = opacity!
    ctx.fillStyle = color!
    ctx.font = effectiveFont
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    ctx.translate(tileW / 2, tileH / 2)
    ctx.rotate(rad)

    // 绘制多行文本
    const totalTextHeight = lineHeight * resolvedLines.length
    const startY = -(totalTextHeight - lineHeight) / 2

    resolvedLines.forEach((line, index) => {
      ctx.fillText(line, 0, startY + index * lineHeight)
    })

    const base64 = canvas.toDataURL()
    return { base64, tileW, tileH }
  }

  /** 创建水印层 */
  private createLayer() {
    const { base64, tileW, tileH } = this.createWatermarkBase64()
    const div = document.createElement('div')
    div.className = 'watermark-layer'

    Object.assign(div.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      backgroundImage: `url(${base64})`,
      backgroundRepeat: 'repeat',
      backgroundSize: `${tileW}px ${tileH}px`,
      zIndex: String(this.options.zIndex)
    })
    document.body.appendChild(div)
    this.el = div
  }

  /** 防删除 */
  private protect() {
    if (!this.el) return

    this.observer = new MutationObserver(() => {
      if (!document.body.contains(this.el!)) {
        document.body.appendChild(this.el!)
      }
    })

    this.observer.observe(document.body, {
      childList: true
    })

    // 防篡改
    this.timer = setInterval(() => {
      if (!this.el) return

      const style = window.getComputedStyle(this.el)

      if (style.display === 'none') {
        this.el.style.display = 'block'
      }

      if (style.zIndex !== String(this.options.zIndex)) {
        this.el.style.zIndex = String(this.options.zIndex)
      }
    }, 2000)
  }

  /** resize / 全屏 重绘 */
  private bindResize() {
    window.addEventListener('resize', this.redraw)
    document.addEventListener('fullscreenchange', this.redraw)
  }

  private redraw = () => {
    this.destroy()
    this.createLayer()
    this.protect()
  }

  /** 创建水印 */
  create() {
    this.createLayer()
    this.protect()
    this.bindResize()
  }

  /** 更新水印 */
  update(text: string | string[]) {
    this.options.text = text
    this.redraw()
  }

  /** 更新配置 */
  updateOptions(options: Partial<WatermarkOptions>) {
    this.options = {
      ...this.options,
      ...options
    }
    this.redraw()
  }

  /** 销毁 */
  destroy() {
    if (this.el) {
      this.el.remove()
      this.el = null
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }

    window.removeEventListener('resize', this.redraw)
    document.removeEventListener('fullscreenchange', this.redraw)
  }
}

export default Watermark
